// Baixa os PDFs originais de um conjunto de faturas num único .zip.
// Fonte dos bytes: o File ainda em memória (upload desta sessão) ou o Storage
// (pdf_storage_path). PDFs já são comprimidos — o zip usa STORE (sem recompressão).
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { getPdfBlob } from '../../../src/services/extracaoApi';

export interface ZipPdfItem {
  nome: string;                 // nome do arquivo dentro do zip
  file?: File;                  // bytes em memória (upload da sessão atual)
  path?: string | null;         // caminho no Storage (histórico/recuperado)
}

const LOTE_PARALELO = 6;

const comPdf = (n: string) => (/\.pdf$/i.test(n) ? n : `${n}.pdf`);

export interface ZipResultado { adicionados: number; faltando: string[]; cancelado?: boolean }

/** Monta e baixa o zip. `onProgress(feitos, total)` roda a cada lote de PDFs resolvido.
 *  `deveCancelar` é checado entre os lotes paralelos: true = aborta sem gerar o zip. */
export async function baixarPdfsZip(
  itens: ZipPdfItem[],
  zipName: string,
  onProgress?: (feitos: number, total: number) => void,
  deveCancelar?: () => boolean,
): Promise<ZipResultado> {
  const alvos = itens.filter(i => i.file || i.path);
  if (alvos.length === 0) return { adicionados: 0, faltando: itens.map(i => i.nome) };

  const zip = new JSZip();
  const usados = new Set<string>();
  const faltando: string[] = itens.filter(i => !i.file && !i.path).map(i => i.nome);
  let feitos = 0;

  // Nome único dentro do zip (lotes têm reenvios com o mesmo nome de arquivo).
  const nomeUnico = (nome: string): string => {
    let n = comPdf(nome.replace(/[\\/:*?"<>|]/g, '_') || 'fatura.pdf');
    for (let i = 2; usados.has(n.toLowerCase()); i++) n = comPdf(nome.replace(/\.pdf$/i, '') + ` (${i})`);
    usados.add(n.toLowerCase());
    return n;
  };

  // Busca em lotes paralelos pequenos — 600+ downloads sequenciais demorariam demais.
  for (let de = 0; de < alvos.length; de += LOTE_PARALELO) {
    if (deveCancelar?.()) return { adicionados: 0, faltando, cancelado: true };
    const fatia = alvos.slice(de, de + LOTE_PARALELO);
    const blobs = await Promise.all(fatia.map(async (i) => {
      try { return i.file ?? await getPdfBlob(i.path!); } catch { return null; }
    }));
    fatia.forEach((i, j) => {
      const blob = blobs[j];
      if (blob) zip.file(nomeUnico(i.nome), blob);
      else faltando.push(i.nome);
      feitos++;
    });
    onProgress?.(feitos, alvos.length);
  }

  const adicionados = usados.size;
  if (adicionados > 0) {
    const blob = await zip.generateAsync({ type: 'blob', compression: 'STORE' });
    saveAs(blob, /\.zip$/i.test(zipName) ? zipName : `${zipName}.zip`);
  }
  return { adicionados, faltando };
}
